import React from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { calculateShippingPrice, getEstimatedDeliveryDate } from '../../util/dates';
import {
  getSelectedShippingMethodIdForGroup,
  SHIPPING_GROUP_PROVIDER_KEY,
} from '../../util/cartProviderGroups';
import css from './ProviderShippingSelectors.module.css';

/**
 * Sélecteur(s) de mode de livraison par groupe d’expédition (même logique que CartPage).
 */
const ProviderShippingSelectors = ({
  group,
  providerSubtotal = 0,
  currency: currencyFallback,
  onUpdateShippingMethod,
  idPrefix = 'shipping',
}) => {
  const intl = useIntl();
  const shippingGroupsData = group.shippingGroups || [];
  if (shippingGroupsData.length === 0) {
    return null;
  }

  const renderShippingSelector = (shippingGroup, applyToProvider, groupIndex = 0) => {
    const shippingMethods = shippingGroup.shippingMethods || [];
    if (!shippingMethods.length) {
      return null;
    }

    const selectedMethodId =
      getSelectedShippingMethodIdForGroup(group, shippingGroup) ||
      shippingMethods[0]?.id ||
      '';

    const handleShippingChange = event => {
      const newShippingMethodId = event.target.value;
      if (!newShippingMethodId) {
        return;
      }
      onUpdateShippingMethod({
        providerId: group.providerId,
        shippingMethodId: newShippingMethodId,
        shippingGroupKey: shippingGroup.key || SHIPPING_GROUP_PROVIDER_KEY,
        applyToProvider,
      });
    };

    const sampleListing =
      group.listings.find(
        listing => listing?.id?.uuid && shippingGroup.listingIds?.includes(listing.id.uuid)
      ) || group.listings.find(listing => listing?.id?.uuid);
    const currency = sampleListing?.attributes?.price?.currency || currencyFallback;

    const safeId = `${idPrefix}-${String(group.providerId).replace(/[^a-zA-Z0-9_-]/g, '')}-${
      shippingGroup.key || 'default'
    }`.replace(/[^a-zA-Z0-9_-]/g, '-');

    return (
      <div
        key={`${group.providerId}-${shippingGroup.key || 'default'}`}
        className={css.providerShippingSelector}
      >
        {shippingGroupsData.length > 1 && (
          <p className={css.shippingGroupLabel}>
            <FormattedMessage
              id="CartPage.shippingGroupLabel"
              defaultMessage="Produits : {products}"
              values={{
                products:
                  shippingGroup.productTitles?.join(', ') ||
                  intl.formatMessage(
                    {
                      id: 'CartPage.shippingGroupDefaultLabel',
                      defaultMessage: 'Groupe {index}',
                    },
                    { index: groupIndex + 1 }
                  ),
              }}
            />
          </p>
        )}
        <label htmlFor={safeId} className={css.shippingMethodLabel}>
          <FormattedMessage
            id="CartPage.shippingMethodLabel"
            defaultMessage="Méthode d'expédition"
          />
        </label>
        <div className={css.selectWrap}>
          <select
            id={safeId}
            className={css.shippingMethodSelect}
            value={selectedMethodId || ''}
            onChange={handleShippingChange}
          >
            {shippingMethods.map(method => {
              const shippingPrice = calculateShippingPrice(method, shippingGroup.totalQuantity);
              const meetsFranco = method.franco && providerSubtotal >= method.franco;
              const priceValue = meetsFranco ? 0 : shippingPrice;
              const formattedPrice =
                currency && typeof priceValue === 'number'
                  ? intl.formatNumber(priceValue / 100, {
                      style: 'currency',
                      currency,
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : '';

              const deliveryDate = method.delay
                ? getEstimatedDeliveryDate(parseInt(method.delay, 10), intl)
                : null;
              const locationLabel =
                method.id === 'standard'
                  ? 'Chez vous'
                  : method.id === 'relay'
                  ? 'En point relais'
                  : method.name;
              const carrierLabel = method.carrier ? ` avec ${method.carrier}` : '';
              const priceLabel = meetsFranco
                ? intl.formatMessage({
                    id: 'CartPage.freeShipping',
                    defaultMessage: 'Gratuit',
                  })
                : formattedPrice;

              return (
                <option key={method.id} value={method.id}>
                  {locationLabel}
                  {deliveryDate ? ` - ${deliveryDate}` : ''}
                  {carrierLabel ? ` ${carrierLabel}` : ''} - {priceLabel}
                </option>
              );
            })}
          </select>
        </div>
      </div>
    );
  };

  if (shippingGroupsData.length === 1) {
    return renderShippingSelector(shippingGroupsData[0], true, 0);
  }

  return (
    <>
      {shippingGroupsData.map((shippingGroup, index) =>
        renderShippingSelector(shippingGroup, false, index)
      )}
    </>
  );
};

export default ProviderShippingSelectors;
